import { useCallback } from 'react';
import { TableWithHeader, TableWithHeaderProps } from './shared/TableWithHeader';
import { DataTableRow } from '../../common/table/TableTypes';
import { humanizeDate } from '../../helpers/humanizeDate';
import pluralize from 'pluralize';
import {
  DocumentationCardContentViewType,
  DocumentationCardItem,
} from '../salesforce-documentation-card/types';
import { InactiveTag } from '../building-blocks/activity-state/InactiveTag';
import { TableTypography } from '../../common/table/utils';
import { TableItemHighlightedText } from '../building-blocks/documentation-card/search/highlight/TableItemHighlightedText';
import { PossibleSectionInDependencies } from '../building-blocks/documentation-card/search/types';
import { SalesforceItem } from '../../parser/ParserTypes';
import { isItemActive } from '../helpers';

interface DefaultConfigurationTableProps extends Pick<TableWithHeaderProps, 'tableHeader'> {
  items: SalesforceItem[];
  onItemClick: (props: DocumentationCardItem) => void;
  parentType: string;
  activeItemId?: string;
  clearHistory?: boolean;
  section?: PossibleSectionInDependencies;
}

const columns = [
  { field: 'label', headerName: 'Name', width: '210px' },
  { field: 'lastModified', headerName: 'Last Modified' },
];

const renderRows = ({
  items,
  activeItemId,
  section = 'dependencies',
  tableType,
}: {
  items: SalesforceItem[];
  activeItemId?: string;
  section?: PossibleSectionInDependencies;
  tableType: string;
}) => {
  return items.map((row, index) => ({
    id: row.id,
    name: row.name,
    isRowActive: activeItemId === row.id || activeItemId === row.name,
    label: (
      <>
        <TableTypography key={row.id}>
          <TableItemHighlightedText
            text={row.label}
            field="label"
            section={section}
            tableType={tableType}
            rowIndex={index}
          />
        </TableTypography>
        <InactiveTag isActive={isItemActive(row)} />
      </>
    ),
    lastModified: (
      <TableTypography key={row.id + row.updatedAt}>
        <TableItemHighlightedText
          text={humanizeDate({ dateOrTimestamp: row.updatedAt ?? '-' })}
          field="lastModified"
          section={section}
          tableType={tableType}
          rowIndex={index}
        />
      </TableTypography>
    ),
    objectName: row.objectApiNames?.[0],
    isClickable: true,
    itemData: row,
  }));
};

export const DefaultConfigurationTable = (props: DefaultConfigurationTableProps) => {
  const {
    items,
    onItemClick,
    tableHeader,
    parentType,
    activeItemId,
    clearHistory,
    section = 'dependencies',
  } = props;
  const onRowClick = useCallback(
    (e: React.MouseEvent<HTMLTableCellElement, MouseEvent>, row: DataTableRow) => {
      onItemClick({
        clearHistory,
        contentType: DocumentationCardContentViewType.default,
        item: {
          ...row.itemData,
          parentType,
        },
      });
    },
    [onItemClick, parentType, clearHistory],
  );

  const rows = renderRows({ items, activeItemId, section, tableType: parentType });

  return (
    <TableWithHeader
      {...props}
      key={`tableHeader_${tableHeader}`}
      tableHeader={pluralize(tableHeader, rows.length)}
      columns={columns}
      rows={rows}
      onRowClick={onRowClick}
      dataTableType={parentType}
      section={section}
    />
  );
};
